// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { ConcentrationGraph } from '@advisor-app/features/risk/components/concentration';
import { type FC, useMemo } from 'react';
import { RiskVsReturnsGraph } from '@shared/ui-kit/components';
import { type StockAnalysis } from '@shared/data-access/interfaces/api/ai/portfolio-analysis';
import { TopAssets, ValueAtRisk } from '@advisor-app/features/risk';
import { TrackingError } from './tracking-error';
import { type AssetsInfoResp } from '@shared/data-access/interfaces/api/ai/assets';
import { type Stock } from '@shared/data-access/interfaces/api/ai/screener';

export interface RiskStocksProps {
  analysis: StockAnalysis;
  assetsInfo: AssetsInfoResp[];
}

export const RiskStocks: FC<RiskStocksProps> = ({ analysis, assetsInfo }) => {
  const companies = useMemo(() => {
    return analysis.top_holdings?.target_asset?.map(asset => {
      const company = assetsInfo.find(
        info => info.ticker === asset.ticker,
      ) as Stock;

      return {
        ticker: asset.ticker,
        weight: asset.weight,
        name: company?.name || '',
        outstandingBalance:
          asset.weight * analysis.capital_gains.outstanding_balance,
        logo: company?.url_logo,
        url: company?.url_morningstar,
        assetType: company?.asset_type,
      };
    });
  }, [
    analysis.top_holdings,
    assetsInfo,
    analysis.capital_gains.outstanding_balance,
  ]);

  return (
    <div className="risk-return-grid" data-testid="risk">
      <ValueAtRisk valueAtRisk={analysis?.var} />
      <RiskVsReturnsGraph risks={analysis?.risk_returns} />
      <ConcentrationGraph concentrations={analysis?.concentration} />
      <TopAssets assets={companies} />
      <div className="col-span-2">
        <TrackingError tracking_expected={analysis.tracking_expected} />
      </div>
    </div>
  );
};
